import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, MessageCircle, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { confirmBookingByToken } from "@/lib/bookings.functions";

const WHATSAPP = [{ label: "+40 738 792 499", wa: "40738792499" }];

export const Route = createFileRoute("/reconfirm/$token")({
  head: () => ({
    meta: [
      { title: "Reconfirm your booking | Car Rental Bucharest" },
      {
        name: "description",
        content: "Confirm your upcoming car rental reservation in Bucharest in one click.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Reconfirm your booking | Car Rental Bucharest" },
      { property: "og:description", content: "Confirm your upcoming car rental reservation." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: ReconfirmPage,
  errorComponent: () => (
    <Shell>
      <XCircle className="size-10 text-red-400" />
      <h1 className="mt-5 font-display text-2xl font-bold">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        We could not confirm your booking. Please message us on WhatsApp at +40 738 792 499 and we will handle it.
      </p>
    </Shell>
  ),
  notFoundComponent: () => (
    <Shell>
      <XCircle className="size-10 text-red-400" />
      <h1 className="mt-5 font-display text-2xl font-bold">Link not found</h1>
    </Shell>
  ),
});

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="container-page flex min-h-[70vh] items-center justify-center py-20">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-card">
        <div className="flex justify-center">{null}</div>
        {children}
      </div>
    </main>
  );
}

function ReconfirmPage() {
  const { token } = Route.useParams();
  const confirm = useServerFn(confirmBookingByToken);
  const query = useQuery({
    queryKey: ["reconfirm", token],
    queryFn: () => confirm({ data: { token } }),
    retry: false,
    staleTime: Infinity,
  });

  if (query.isLoading) {
    return (
      <Shell>
        <p className="text-sm text-muted-foreground">Confirming your booking…</p>
      </Shell>
    );
  }

  if (query.isError || query.data?.status === "invalid") {
    return (
      <Shell>
        <div className="flex justify-center">
          <XCircle className="size-10 text-red-400" />
        </div>
        <h1 className="mt-5 font-display text-2xl font-bold">This link is not valid</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The confirmation link may have expired or already been replaced. Please contact us and we
          will send a new one.
        </p>
        <div className="mt-6 flex flex-col gap-2">
          {WHATSAPP.map((n) => (
            <a
              key={n.wa}
              href={`https://wa.me/${n.wa}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-gold hover:text-gold"
            >
              <MessageCircle className="size-4" /> {n.label}
            </a>
          ))}
        </div>
      </Shell>
    );
  }

  const data = query.data;
  const already = data?.status === "already";

  return (
    <Shell>
      <div className="flex justify-center">
        <CheckCircle2 className="size-10 text-gold" />
      </div>
      <p className="eyebrow mt-5 text-gold">Car Rental Bucharest</p>
      <h1 className="mt-2 font-display text-2xl font-bold">
        {already ? "Already confirmed — thank you!" : "Thanks, your booking is confirmed!"}
      </h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {data && "carName" in data ? (
          <>
            {data.customerName}, your reservation for <strong className="text-foreground">{data.carName}</strong>{" "}
            starting {data.pickupDate} is reconfirmed. Nothing else is needed before pickup.
          </>
        ) : (
          "Your reservation is reconfirmed."
        )}
      </p>

      <div className="mt-6 rounded-xl border border-border bg-background/50 p-4 text-left">
        <p className="eyebrow text-gold">Questions before pickup?</p>
        <p className="mt-2 text-sm text-muted-foreground">
          We are available on WhatsApp — flight delays, extra drivers or directions.
        </p>
        <div className="mt-3 flex flex-col gap-2">
          {WHATSAPP.map((n) => (
            <a
              key={n.wa}
              href={`https://wa.me/${n.wa}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-gold hover:text-gold"
            >
              <MessageCircle className="size-4" /> {n.label}
            </a>
          ))}
          <a
            href="mailto:book@carentals.ro"
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm hover:border-gold hover:text-gold"
          >
            book@carentals.ro
          </a>
        </div>
      </div>

      <Button asChild variant="outline" className="mt-6">
        <Link to="/">Back to homepage</Link>
      </Button>
    </Shell>
  );
}
